import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { BottomData } from '../styles/styles';
import {
  Header,
  ContentHeading,
  RightContent,
  LeftContent,
  Content,
  DateDropBox,
  DropdownButton,
  HeaderLeft,
  HeaderTitle
} from '../styles';
import TableHeader from '../../../component/tableHeader';
import LineChart from '../../../component/chart';
import { loadTimeLogs } from '../../../services/timeTrackingServices';
import { ReportFilterInterface } from '../../../interfaces/TimeTrackingInterface';
import {
  calculateEndTime,
  checkIsAllBoxChecked,
  floatToHHMM,
  getDateRange,
  getMonthRange,
  getUTCDate,
  isEmpty
} from '../../../helpers/common';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  CHIP_ICON_POSITION,
  CHIP_SIZE,
  CHIP_TYPE,
  PREFERENCE_WRITE_DEBOUNCE_MS,
  TIME_DATE_SELECTED_TEXT,
  TIME_REPORT_CHART_TYPE_ID,
  TIME_REPORT_DATA_FILTER_ID,
  TIME_REPORT_VIEW_TYPE_ID
} from '../../../global/constants';
import {
  WorkSummaryInterface,
  TimeLogInterface,
  WorkSummaryItemInterface,
  TableData,
  SelectionRangeInterface,
  ActivityDataInterface,
  ChartDataInterface,
  ChartDetailsInterface
} from '../../../interfaces/TimeReportInterface';
import { useHistory } from 'react-router-dom';
import { getUserWorkspacePreferenceData, updateUserWorkspacePreference } from '../../../helpers/firebaseHelper';
import {
  ProjectListInterface,
  ReportTimeFilterInterfaceV2,
  SelectRangeInterface,
  UserCheckListInterface
} from '../../../interfaces/ReportInterface';
import ReportTimelineLoading from '../../../component/loading/reportTimeLineLoading';
import ReportDurationDropdown from '../../../component/dropdowns/reportDurationDropdown';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { RawTimeTrackingDataInterface } from '../../../mobx/interfaces/task';
import { useMobileDevice } from '../../../global/useMobile';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import Chip from '../../../component/chipComponent';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { captureException } from '../../../services/logService';
import { ROUTES } from "../../../routes/urls";

const Time: React.FC = () => {
  //Refs
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const outsideRef = useRef<HTMLDivElement | null>(null);
  const dateRef = useRef<HTMLDivElement | null>(null);
  //States
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [searchProject, setSearchProject] = useState('');
  const [searchUser, setSearchUser] = useState('');

  const [selectAllUser, setSelectAllUser] = useState(true);
  const [selectAllProject, setSelectAllProject] = useState(true);

  const [projectWiseChartData, setProjectWiseChartData] = useState<ChartDataInterface>({ data: [], x_axis_data: [] });
  const [timeSheetData, setTimeSheetData] = useState<TableData>({});
  const [activityData, setActivityData] = useState<ActivityDataInterface[]>([]);
  const [workSummaryData, setWorkSummaryData] = useState<WorkSummaryInterface[]>([]);

  const [time_filter, set_time_filter] = useState<ReportTimeFilterInterfaceV2>();
  const [filterProjectList, setFilterProjectList] = useState<ProjectListInterface[]>([]);
  const [filterUserList, setFilterUserList] = useState<UserCheckListInterface[]>([]);

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  //Mobx store variables
  const { projectStore, workspaceStore, reportStore } = rootStore;
  const { projects } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { reportFilter, timeTrackingReportData } = reportStore;

  //Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();

  // Debounce Firebase preference writes: local state updates immediately for instant UI feedback,
  // while rapid-fire filter toggles (user/project/date) are coalesced into a single Firestore commit.
  // The workspace id is captured at schedule time and passed explicitly, so a pending/flushed write
  // always lands in the workspace that was active when it was scheduled — never the one switched to.
  const debouncedWritePreference = useRef(
    debounce((data: ReportTimeFilterInterfaceV2, workspaceId?: string) => {
      void updateUserWorkspacePreference({ time_report_filter: data }, workspaceId);
    }, PREFERENCE_WRITE_DEBOUNCE_MS)
  ).current;

  // Flush any pending preference write on unmount so the last change isn't lost.
  useEffect(() => () => debouncedWritePreference.flush(), [debouncedWritePreference]);

  // Add useEffect for outside click handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        outsideRef.current &&
        !outsideRef.current.contains(targetElement) &&
        !targetElement.closest('.calendar-dropdown') &&
        openDropdown === 'Date' &&
        !dateRef.current?.contains(targetElement)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const processWorkSummary = useCallback(
    (
      logData: RawTimeTrackingDataInterface[],
      projectList: ProjectListInterface[],
      users: UserCheckListInterface[],
      reportFilter: ReportFilterInterface
    ) => {
      if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT) {
        let project_work_summary: WorkSummaryInterface[] = [];
        projectList?.forEach((item) => {
          let total_time = 0;
          let items: WorkSummaryItemInterface[] = [];

          users?.forEach((ele) => {
            const user = workspace?.users?.find((user) => user?.id === ele?.id);
            const memberTask = logData?.filter((x) => x?.createdBy === ele?.id && x?.projectId === item?.id);
            const memberHour = memberTask.reduce((acc: number, obj: { hours: number }) => acc + obj.hours, 0);

            if (memberHour > 0) {
              total_time += memberHour;
              items.push({
                id: ele?.id,
                name: user?.name || '',
                time: floatToHHMM(memberHour),
                amount: 0,
                currency: 'USD'
              });
            }
          });
          if (total_time > 0) {
            project_work_summary?.push({
              id: item?.id,
              name: rootStore.projectStore.projects?.find((project) => project?.id === item?.id)?.name || '',
              amount: 0,
              time: floatToHHMM(total_time),
              items
            });
          }
        });
        setWorkSummaryData(project_work_summary);
      } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER) {
        let member_work_summary: WorkSummaryInterface[] = [];
        users?.forEach((item) => {
          let total_time = 0;
          let items: WorkSummaryItemInterface[] = [];
          const user = workspace?.users?.find((user) => user?.id === item?.id);

          projectList?.forEach((ele) => {
            const project = projects?.find((project) => project?.id === ele?.id);
            const projectLogs = logData?.filter((x) => x?.projectId === ele?.id && x?.createdBy === item?.id);
            const projectHour = projectLogs.reduce((acc: number, obj: { hours: number }) => acc + obj.hours, 0);

            if (projectHour > 0) {
              total_time += projectHour;
              items.push({
                id: ele?.id,
                name: project?.name || '',
                time: floatToHHMM(projectHour),
                amount: 0,
                currency: 'USD'
              });
            }
          });

          if (total_time > 0) {
            member_work_summary?.push({
              id: item?.id,
              name: user?.name || '',
              amount: 0,
              time: floatToHHMM(total_time),
              items
            });
          }
        });
        setWorkSummaryData(member_work_summary);
      } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_ACTIVITY) {
        let activity_work_summary: WorkSummaryInterface[] = [];
        users?.forEach((item) => {
          let total_time = 0;
          const user = workspace?.users?.find((user) => user?.id === item?.id);
          let items: WorkSummaryItemInterface[] = [];
          const userTimeLogs = logData?.filter((x) => x?.createdBy === item?.id);

          const groupedData = userTimeLogs.reduce(
            (acc: Record<string, RawTimeTrackingDataInterface[]>, obj: RawTimeTrackingDataInterface) => {
              const key = obj.taskId;
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(obj);
              return acc;
            },
            {}
          );
          Object.keys(groupedData).forEach((taskId) => {
            const totalHour = groupedData[taskId].reduce((acc: number, obj: { hours: number }) => {
              return acc + obj.hours;
            }, 0);
            if (totalHour > 0) {
              total_time = total_time + totalHour;
              const obj = {
                id: taskId,
                name: groupedData[taskId][0]?.taskName,
                time: floatToHHMM(totalHour),
                amount: 0,
                currency: 'USD'
              };
              items?.push(obj);
            }
          });
          if (total_time > 0) {
            activity_work_summary?.push({
              id: item?.id,
              name: user?.name || '',
              amount: 0,
              time: floatToHHMM(total_time),
              items
            });
          }
        });
        setWorkSummaryData(activity_work_summary);
      } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS) {
        const labelGroups: {
          [key: string]: { total_time: number; items: { [taskId: string]: { name: string; hours: number } } };
        } = {};

        logData.forEach((log) => {
          const primaryLabel = log.labels && log.labels.length > 0 ? log.labels[0].name : 'No Label';

          if (!labelGroups[primaryLabel]) {
            labelGroups[primaryLabel] = { total_time: 0, items: {} };
          }

          if (!labelGroups[primaryLabel].items[log.taskId]) {
            labelGroups[primaryLabel].items[log.taskId] = {
              name: log.taskName,
              hours: 0
            };
          }

          labelGroups[primaryLabel].total_time += log.hours;
          labelGroups[primaryLabel].items[log.taskId].hours += log.hours;
        });

        const labels_work_summary: WorkSummaryInterface[] = Object.keys(labelGroups).map((labelName) => {
          const group = labelGroups[labelName];
          const items: WorkSummaryItemInterface[] = Object.keys(group.items).map((taskId) => {
            const taskItem = group.items[taskId];
            return {
              id: taskId,
              name: taskItem.name,
              time: floatToHHMM(taskItem.hours),
              amount: 0,
              currency: 'USD'
            };
          });
          return {
            id: labelName,
            name: labelName,
            amount: 0,
            time: floatToHHMM(group.total_time),
            items
          };
        });
        setWorkSummaryData(labels_work_summary);
      }
    },
    [workspace?.users, projects]
  );

  const processTimeSheet = useCallback(
    (
      logData: RawTimeTrackingDataInterface[],
      projectList: ProjectListInterface[],
      users: UserCheckListInterface[],
      selectionRange: SelectionRangeInterface,
      reportFilter: ReportFilterInterface
    ) => {
      const getTimeSheetColumns = () => {
        let columns = [
          {
            Header:
              reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT
                ? 'Projects Name'
                : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER
                ? 'Member Name'
                : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS
                ? 'Label Name'
                : 'Activity Name',
            accessor: 'name'
          }
        ];
        let startDateRef = dayjs(selectionRange.startDate);
        while (startDateRef.isBefore(dayjs(selectionRange.endDate).add(1, 'day'), 'day')) {
          columns.push({
            Header: startDateRef.format('MMM DD'),
            accessor: `date${startDateRef.format('DD_MM_YYYY')}`
          });
          startDateRef = startDateRef.add(1, 'day');
        }
        columns.push({ Header: 'Total', accessor: 'total_time' });
        return columns;
      };

      const columns = getTimeSheetColumns();
      let timesheet_data: Record<string, string | number>[] = [];
      let totalObj: Record<string, number> = {};

      let items;
      if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT) {
        items = projectList;
      } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER) {
        items = users;
      } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS) {
        // For labels view, get a unique list of FIRST labels
        const firstLabels = new Set<string>();
        let hasNoLabelLogs = false;

        logData?.forEach((log) => {
          if (log.labels && log.labels.length > 0) {
            firstLabels.add(log.labels[0].name); // Only add the first label
          } else {
            hasNoLabelLogs = true;
          }
        });

        items = Array.from(firstLabels).map((label) => ({
          id: label,
          name: label
        }));

        if (hasNoLabelLogs) {
          items.push({ id: 'no-label', name: 'No Label' });
        }
      } else {
        // For activity view, group by taskId
        const groupedByTask = logData.reduce(
          (acc: Record<string, RawTimeTrackingDataInterface[]>, obj: RawTimeTrackingDataInterface) => {
            const key = obj.taskId;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(obj);
            return acc;
          },
          {}
        );
        items = Object.keys(groupedByTask).map((taskId) => ({
          id: taskId,
          name: groupedByTask[taskId][0]?.taskName
        }));
      }

      items?.forEach((item) => {
        let total_time = 0;
        const itemName =
          reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT
            ? projects?.find((project) => project?.id === item?.id)?.name
            : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER
            ? workspace?.users?.find((user) => user?.id === item?.id)?.name
            : reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS
            ? item.name
            : item.name;

        let data: Record<string, string | number> = { name: itemName || '' };
        let startDateRef = dayjs(selectionRange.startDate);
        let endDateRef = dayjs(selectionRange.endDate).add(1, 'day');

        while (startDateRef.isBefore(endDateRef, 'day')) {
          const currentDateISO = getUTCDate(startDateRef).toISOString();
          const logs = logData?.filter((x) => {
            let matchId = false;
            if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_PROJECT) {
              matchId = x?.projectId === item?.id;
            } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_MEMBER) {
              matchId = x?.createdBy === item?.id;
            } else if (reportFilter?.timeViewType === TIME_REPORT_VIEW_TYPE_ID.BY_LABELS) {
              if (item?.id === 'no-label') {
                // For the "No Label" row, find tasks with no labels
                matchId = !x.labels || x.labels.length === 0;
              } else {
                // For regular label rows
                matchId = !!(x.labels && x.labels.length > 0 && x.labels[0].name === item?.id);
              }
            } else {
              // BY_ACTIVITY
              matchId = x?.taskId === item?.id;
            }
            return matchId && getUTCDate(x?.date).toISOString() === currentDateISO;
          });

          const totalHours = logs.reduce((acc: number, obj: { hours: number }) => acc + obj.hours, 0);
          const dateKey = `date${startDateRef.format('DD_MM_YYYY')}`;

          data[dateKey] = totalHours > 0 ? floatToHHMM(totalHours) : '--';
          totalObj[dateKey] = (totalObj[dateKey] || 0) + totalHours;
          total_time += totalHours;

          startDateRef = startDateRef.add(1, 'day');
        }

        data.total_time_float = total_time;
        data.total_time = total_time > 0 ? floatToHHMM(total_time) : '--';
        totalObj['total_time'] = (totalObj['total_time'] || 0) + total_time;

        timesheet_data.push(data);
      });

      timesheet_data = timesheet_data.toSorted((a, b) => Number(b.total_time_float) - Number(a.total_time_float));

      const totalRow: Record<string, string | number> = { name: 'Total' };
      Object.keys(totalObj).forEach((key) => {
        const value = totalObj[key] || 0;
        totalRow[key] = value > 0 ? floatToHHMM(value) : '--';
      });

      if (timesheet_data?.length > 0) {
        timesheet_data.push(totalRow);
      }

      setTimeSheetData({ column: columns, data: timesheet_data });
    },
    [projects, workspace?.users]
  );

  const processActivityLog = useCallback((logData: RawTimeTrackingDataInterface[]) => {
    const activityData = logData
      ?.map((item) => {
        if (item?.history?.length > 0) {
          const maxTimerEndedAt = Math.max(...item.history.map((h) => new Date(h.TimerEndedAt).getTime()));
          const minTimerStartedAt = Math.min(
            ...item.history.map((h) => new Date(h.TimerStartedAt || new Date()).getTime()).filter((x) => !isEmpty(x))
          );
          const minTimerStartedAtString = new Date(minTimerStartedAt).toISOString();

          return {
            date: dayjs(item?.date).format('MMM DD'),
            time: `${dayjs(minTimerStartedAtString).format('h:mma')} - ${dayjs(maxTimerEndedAt).format('h:mma')}`,
            startTime: dayjs(minTimerStartedAtString).format('YYYY-MM-DD HH:mm:ss'),
            endTime: dayjs(calculateEndTime(minTimerStartedAtString.toString(), item?.hours)).format(
              'YYYY-MM-DD HH:mm:ss'
            ),
            activityName: item?.taskName,
            member: item?.userName,
            projectName: item?.projectName,
            duration: floatToHHMM(item?.hours),
            screenshots: item?.screenshots?.length || 0,
            screenshotImage: item?.screenshots?.[0]?.Url || '',
            screenshotImageList: item?.screenshots || []
          };
        } else if (item?.hours > 0) {
          return {
            date: dayjs(item?.date).format('MMM DD'),
            time: `${dayjs(item?.createdOn).format('h:mma')} - ${dayjs(item?.createdOn)
              .add(item?.hours, 'hours')
              .format('h:mma')}`,
            startTime: dayjs(item?.createdOn).format('YYYY-MM-DD HH:mm:ss'),
            endTime: dayjs(calculateEndTime(item?.createdOn.toString(), item?.hours)).format('YYYY-MM-DD HH:mm:ss'),
            activityName: item?.taskName,
            member: item?.userName,
            projectName: item?.projectName,
            duration: floatToHHMM(item?.hours),
            screenshots: item?.screenshots?.length || 0,
            screenshotImage: item?.screenshots?.[0]?.Url || '',
            screenshotImageList: item?.screenshots || []
          };
        }
        return null;
      })
      .filter((item): item is ActivityDataInterface => item !== null);

    setActivityData(activityData);
  }, []);

  const loadDataBasedOnFilter = useCallback(
    (
      logData: RawTimeTrackingDataInterface[],
      selectedProjects: ProjectListInterface[],
      users: UserCheckListInterface[],
      selectionRange: SelectionRangeInterface,
      reportFilter: ReportFilterInterface
    ) => {
      try {
        setFilterLoading(true);
        const selectedProjectIds = selectedProjects?.map((x) => x?.id);
        const selectedUsersIds = users?.map((x) => x?.id);
        logData = logData?.filter(
          (item) =>
            selectedProjectIds?.includes(item?.projectId) &&
            selectedUsersIds?.includes(item?.createdBy) &&
            dayjs(item?.date) >= dayjs(selectionRange.startDate) &&
            dayjs(item?.date) <= dayjs(selectionRange.endDate).add(1, 'day')
        );
        if (reportFilter?.selectedDataType === TIME_REPORT_DATA_FILTER_ID.WORKSUMMARY) {
          processWorkSummary(logData, selectedProjects, users, reportFilter);
        } else if (reportFilter?.selectedDataType === TIME_REPORT_DATA_FILTER_ID.TIMESHEET) {
          processTimeSheet(logData, selectedProjects, users, selectionRange, reportFilter);
        }

        processActivityLog(logData);
      } catch (error) {
        console.log('error', error);
      } finally {
        setFilterLoading(false);
      }
    },
    [processActivityLog, processWorkSummary, processTimeSheet]
  );

  const applyFilter = useCallback(
    async (
      filterProjectList: ProjectListInterface[],
      users: UserCheckListInterface[],
      selectionRange: SelectRangeInterface,
      filterObj: ReportFilterInterface = reportFilter,
      timeReportData: RawTimeTrackingDataInterface[] = timeTrackingReportData
    ) => {
      const selectedProjects = filterProjectList?.filter((x) => x?.checked);
      const selectedUsers = users?.filter((x) => x?.checked);
      const selectedProjectIds = selectedProjects?.map((x) => x?.id);
      const selectedUsersIds = selectedUsers?.map((x) => x?.id);

      const startDate = dayjs(selectionRange.startDate);
      const endDate = dayjs(selectionRange.endDate).add(1, 'day');

      // Filter time log data
      const timeLogData = JSON.parse(JSON.stringify(timeReportData))?.filter(
        (item: TimeLogInterface) =>
          selectedProjectIds?.includes(item?.projectId) &&
          selectedUsersIds?.includes(item?.createdBy) &&
          dayjs(item?.date) >= startDate &&
          dayjs(item?.date) <= endDate
      );

      // Load filtered data
      loadDataBasedOnFilter(timeLogData, selectedProjects, selectedUsers, selectionRange, filterObj);

      // Generate chart data
      const chart_data: ChartDetailsInterface[] = [];
      const items = filterObj?.chartType === TIME_REPORT_CHART_TYPE_ID.PROJECT ? selectedProjects : selectedUsers;

      items?.forEach((item) => {
        const itemDetails =
          filterObj?.chartType === TIME_REPORT_CHART_TYPE_ID.PROJECT
            ? rootStore.projectStore.projects?.find((project) => project?.id === item?.id)
            : workspace?.users?.find((user) => user?.id === item?.id);

        const data = [];
        let startDateRef = dayjs(selectionRange.startDate);

        while (startDateRef.isBefore(endDate, 'day')) {
          const dateStr = startDateRef.format('YYYY-MM-DD');
          const logs = timeLogData?.filter(
            (x: TimeLogInterface) =>
              (filterObj?.chartType === TIME_REPORT_CHART_TYPE_ID.PROJECT ? x?.projectId : x?.createdBy) === item?.id &&
              dayjs(x?.date).format('YYYY-MM-DD') === dateStr
          );

          const totalHours = logs.reduce((acc: number, obj: { hours: number }) => acc + obj.hours, 0);
          data.push(floatToHHMM(totalHours));
          startDateRef = startDateRef.add(1, 'day');
        }

        chart_data.push({
          id: item?.id,
          name: itemDetails?.name || '',
          data
        });
      });

      // Generate x-axis labels
      const x_axis_data: string[] = [];
      let labelDateRef = dayjs(selectionRange.startDate);
      while (labelDateRef.isBefore(endDate, 'day')) {
        x_axis_data.push(labelDateRef.format('DD MMM'));
        labelDateRef = labelDateRef.add(1, 'day');
      }

      setProjectWiseChartData({ data: chart_data, x_axis_data });
    },
    [reportFilter, timeTrackingReportData, loadDataBasedOnFilter, workspace?.users]
  );

  const loadData = useCallback(async () => {
    if (isEmpty(workspace?.id)) return;

    try {
      setLoading(true);

      // Get time report filter and data
      const result = await getUserWorkspacePreferenceData(`time_report_filter`);
      const monthRange = getMonthRange(result?.selected_range?.startDate, result?.selected_range?.endDate);
      const logList = await loadTimeLogs(workspace?.id, monthRange);

      // Generate project and user lists
      const updatedProjectsResponse = projects?.map((item) => ({ id: item.id, checked: true }));
      const usersList = workspace?.users?.map((item) => ({ id: item.id, checked: true }));

      // Check for new projects/users
      const hasNewProjects =
        !result?.project_list ||
        updatedProjectsResponse.some((p) => !result.project_list.some((rp: { id: string }) => rp.id === p.id));
      const hasNewUser =
        !result?.user_list || usersList?.some((u) => !result.user_list.some((ru: { id: string }) => ru.id === u.id));

      // Build filter data object
      const defaultRange = {
        startDate: dayjs().startOf('day').toISOString(),
        endDate: dayjs().startOf('day').toISOString(),
        key: 'selection'
      };

      let data = {
        ...(result || {}),
        project_list: hasNewProjects ? updatedProjectsResponse : result?.project_list,
        user_list: hasNewUser ? usersList : result?.user_list,
        selected_text: result?.selected_text || 'Custom',
        selected_range: result?.selected_range
          ? {
              startDate: new Date(result.selected_range.startDate).toISOString(),
              endDate: new Date(result.selected_range.endDate).toISOString(),
              key: result.selected_range.key
            }
          : defaultRange,
        selected_all_user: result?.selected_all_user ?? true,
        selected_all_project: result?.selected_all_project ?? true
      };

      // Update range if not custom
      if (data.selected_text !== 'Custom') {
        data.selected_range = getDateRange(data.selected_text);
      }

      // Update state
      set_time_filter(data);
      setFilterProjectList(hasNewProjects ? projects : result?.project_list);
      setFilterUserList(hasNewUser ? usersList : result?.user_list);

      setSelectAllUser(checkIsAllBoxChecked(data?.user_list || usersList));
      setSelectAllProject(checkIsAllBoxChecked(data?.project_list || projects));

      // Apply filters and update preferences
      await applyFilter(
        data?.project_list || projects,
        data?.user_list || usersList,
        {
          startDate: new Date(data?.selected_range?.startDate || Date.now()),
          endDate: new Date(data?.selected_range?.endDate || Date.now()),
          key: data?.selected_range?.key || 'selection'
        },
        undefined,
        logList
      );
      debouncedWritePreference(data, workspace?.id);
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  }, [applyFilter, projects, workspace?.id, workspace?.users, debouncedWritePreference]);

  useEffect(() => {
    if (!isEmpty(workspace?.id) && !workspace?.isAdmin) return history.push(ROUTES.INBOX);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, workspace?.isAdmin, workspace?.id]);

  const onChangeReportFilter = useCallback(
    async (propsName: string, value: number) => {
      rootStore.reportStore.updateReportFilter(propsName, value);
      const filterClone = JSON.parse(JSON.stringify(reportFilter));
      filterClone[propsName] = value;
      if (time_filter?.selected_range) {
        loadDataBasedOnFilter(
          timeTrackingReportData,
          time_filter?.project_list?.filter((x) => x?.checked) || [],
          time_filter?.user_list?.filter((x) => x?.checked) || [],
          time_filter?.selected_range,
          filterClone
        );
      }
    },
    [reportFilter, loadDataBasedOnFilter, timeTrackingReportData, time_filter]
  );

  const onChangeSearchProject = useCallback(
    async (value: string) => {
      setSearchProject(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            time_filter?.project_list?.filter((item) => {
              const project = projects?.find((project) => project?.id === item?.id);
              return project?.name?.toLowerCase().includes(value?.toLowerCase());
            }) || [];
          setFilterProjectList(newList);
        }, 300);
      } else {
        if (time_filter?.project_list) setFilterProjectList(time_filter?.project_list);
      }
    },
    [projects, time_filter?.project_list]
  );

  const onSelectAllProjectClick = useCallback(
    async (projectList: ProjectListInterface[], value: boolean, users: UserCheckListInterface[]) => {
      let projectListClone: ProjectListInterface[] = JSON.parse(JSON.stringify(projectList));
      projectListClone = projectListClone?.map((item) => {
        return { ...item, checked: value };
      });
      const data = {
        ...(time_filter || {}),
        project_list: projectListClone,
        selected_all_project: value
      };
      setFilterProjectList(projectListClone);
      setOpenDropdown(null);
      setSelectAllProject(value);
      if (data) set_time_filter(data);
      if (time_filter?.selected_range) applyFilter(projectListClone, users, time_filter?.selected_range);
      // Update time report filter in workspace preference (debounced)
      debouncedWritePreference(data, workspace?.id);
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  const onCheckProject = useCallback(
    async (
      allProjectList: ProjectListInterface[],
      filteredList: ProjectListInterface[],
      projectId: string,
      value: boolean,
      users: UserCheckListInterface[]
    ) => {
      const updatedAllList = allProjectList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const uncheckedTasks = updatedAllList?.find((x) => !x?.checked);
      let newValue;
      if (uncheckedTasks || updatedFilteredList?.length === 0) {
        newValue = false;
      } else {
        newValue = true;
      }

      const data = {
        ...(time_filter || {}),
        project_list: updatedAllList,
        selected_all_project: newValue
      };
      setFilterProjectList(updatedFilteredList);
      setOpenDropdown(null);
      setSelectAllProject(checkIsAllBoxChecked(updatedAllList));
      if (data) set_time_filter(data);
      if (time_filter?.selected_range) applyFilter(updatedAllList, users, time_filter?.selected_range);
      // Update time report filter in workspace preference (debounced)
      debouncedWritePreference(data, workspace?.id);
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  //user list actions
  const onChangeSearchUser = useCallback(
    async (value: string) => {
      setSearchUser(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            time_filter?.user_list?.filter((item: { id: string }) =>
              workspace?.users
                ?.find((project) => project?.id === item?.id)
                ?.name?.toLowerCase()
                .includes(value?.toLowerCase())
            ) || [];
          setFilterUserList(newList);
        }, 300);
      } else {
        if (time_filter?.user_list) setFilterUserList(time_filter?.user_list);
      }
    },
    [time_filter?.user_list, workspace?.users]
  );

  const onSelectAllUserClick = useCallback(
    async (userList: UserCheckListInterface[], value: boolean, projects: ProjectListInterface[]) => {
      let usersListClone: UserCheckListInterface[] = JSON.parse(JSON.stringify(userList));
      usersListClone = usersListClone?.map((item) => {
        return { ...item, checked: value };
      });

      const data = {
        ...(time_filter || {}),
        user_list: usersListClone,
        selected_all_user: value
      };
      setFilterUserList(usersListClone);
      setOpenDropdown(null);
      setSelectAllUser(value);
      set_time_filter(data);
      if (time_filter?.selected_range) applyFilter(projects, usersListClone, time_filter?.selected_range);
      // Update time report filter in workspace preference (debounced)
      debouncedWritePreference(data, workspace?.id);
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  const onCheckUser = useCallback(
    async (
      allUserList: UserCheckListInterface[],
      filteredList: UserCheckListInterface[],
      userId: string,
      value: boolean,
      projects: ProjectListInterface[]
    ) => {
      const updatedAllList = allUserList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const uncheckedTasks = updatedAllList?.find((x) => !x?.checked);
      let newValue;
      if (uncheckedTasks || updatedFilteredList?.length === 0) {
        newValue = false;
      } else {
        newValue = true;
      }

      const data = {
        ...(time_filter || {}),
        user_list: updatedAllList,
        selected_all_user: newValue
      };
      setFilterUserList(updatedFilteredList);
      setOpenDropdown(null);
      setSelectAllUser(checkIsAllBoxChecked(updatedAllList));
      if (data) set_time_filter(data);
      if (time_filter?.selected_range) applyFilter(projects, updatedAllList, time_filter?.selected_range);
      // Update time report filter in workspace preference (debounced)
      debouncedWritePreference(data, workspace?.id);
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  const updateDateRangeChange = useCallback(
    async (startDate: Date, endDate: Date, selected_text: string) => {
      try {
        const selecteDates: SelectionRangeInterface = {
          startDate: startDate,
          endDate: endDate,
          key: 'selection'
        };
        setOpenDropdown(null);
        const data: ReportTimeFilterInterfaceV2 = {
          ...(time_filter || {}),
          selected_range: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            key: 'selection'
          },
          selected_text: selected_text
        };
        set_time_filter(data);
        if (time_filter?.project_list && time_filter?.user_list) {
          const monthRange =
            data?.selected_range?.startDate && data?.selected_range?.endDate
              ? getMonthRange(data.selected_range.startDate, data.selected_range.endDate)
              : [];
          const logList = await loadTimeLogs(workspace?.id, monthRange);
          applyFilter(time_filter?.project_list, time_filter?.user_list, selecteDates, undefined, logList);
        }
        // Update time report filter in workspace preference (debounced)
        debouncedWritePreference(data, workspace?.id);
      } catch (e) {
        captureException(e);
      }
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  const openDateDropdown = useCallback(() => {
    if (loading) return;
    setOpenDropdown(openDropdown === 'Date' ? null : 'Date');
  }, [loading, openDropdown]);

  const handleSelect = useCallback(
    async (ranges: { selection: { startDate: Date; endDate: Date; key: string } }) => {
      try {
        setDataLoading(true);
        const data = {
          ...(time_filter || {}),
          selected_range: {
            startDate: new Date(ranges.selection.startDate).toISOString(),
            endDate: new Date(ranges.selection.endDate).toISOString(),
            key: ranges.selection.key
          },
          selected_text: TIME_DATE_SELECTED_TEXT.CUSTOM
        };
        set_time_filter(data);
        if (time_filter?.project_list && time_filter?.user_list) {
          const monthRange = getMonthRange(data?.selected_range?.startDate, data?.selected_range?.endDate);
          const logList = await loadTimeLogs(workspace?.id, monthRange);
          applyFilter(time_filter?.project_list, time_filter?.user_list, ranges.selection, undefined, logList);
        }
        // Update time report filter in workspace preference (debounced)
        debouncedWritePreference(data, workspace?.id);
      } catch (e) {
        captureException(e);
      } finally {
        setDataLoading(false);
      }
    },
    [applyFilter, time_filter, workspace?.id, debouncedWritePreference]
  );

  const onChangeChartType = useCallback(
    (value: number) => {
      rootStore.reportStore.updateReportFilter('chartType', value);
      const filterClone = JSON.parse(JSON.stringify(reportFilter));
      filterClone['chartType'] = value;
      if (time_filter?.project_list && time_filter?.user_list && time_filter?.selected_range) {
        applyFilter(time_filter?.project_list, time_filter?.user_list, time_filter?.selected_range, filterClone);
      }
    },
    [reportFilter, applyFilter, time_filter]
  );

  const renderProjectData = useMemo(() => {
    return (
      <BottomData isBorderRadius isLoading={loading}>
        {isEmpty(searchProject) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllProject}
            onClick={() =>
              onSelectAllProjectClick(
                time_filter?.project_list || [],
                !time_filter?.selected_all_project,
                time_filter?.user_list || []
              )
            }
          />
        )}
        {(isEmpty(searchProject) ? time_filter?.project_list : filterProjectList)?.map((item: ProjectListInterface) => {
          const project = projects?.find((project) => project?.id === item?.id);
          return (
            <DropdownItem
              key={item?.id}
              label={project?.name}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={(e) =>
                onCheckProject(
                  time_filter?.project_list || [],
                  filterProjectList,
                  item?.id,
                  !item?.checked,
                  time_filter?.user_list || []
                )
              }
            />
          );
        })}
      </BottomData>
    );
  }, [
    searchProject,
    filterProjectList,
    time_filter?.project_list,
    time_filter?.selected_all_project,
    time_filter?.user_list,
    loading,
    selectAllProject,
    onSelectAllProjectClick,
    projects,
    onCheckProject
  ]);

  const renderUserData = useMemo(() => {
    return (
      <BottomData isLoading={loading}>
        {isEmpty(searchUser) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllUser}
            onClick={() =>
              onSelectAllUserClick(filterUserList, !time_filter?.selected_all_user, time_filter?.project_list || [])
            }
          />
        )}
        {(isEmpty(searchUser) ? time_filter?.user_list : filterUserList)?.map((item: UserCheckListInterface) => {
          const user = workspace?.users?.find((user) => user?.id === item?.id);
          return (
            <DropdownItem
              label={user?.name}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={(e) =>
                onCheckUser(
                  time_filter?.user_list || [],
                  filterUserList,
                  item?.id,
                  !item?.checked,
                  time_filter?.project_list || []
                )
              }
            />
          );
        })}
      </BottomData>
    );
  }, [
    searchUser,
    filterUserList,
    time_filter?.user_list,
    time_filter?.selected_all_user,
    time_filter?.project_list,
    loading,
    selectAllUser,
    onSelectAllUserClick,
    workspace?.users,
    onCheckUser
  ]);

  const renderFilterOptions = useMemo(() => {
    return (
      <ContentHeading>
        <LeftContent>
          {/* project dropdown */}
          <Dropdown
            modalTitle='Project'
            onChangeSearch={onChangeSearchProject}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Project'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'Project' ? 'dropdown-icon ' : ''}
                onClick={() => setOpenDropdown(openDropdown === 'Project' ? null : 'Project')}
                isActive={openDropdown === 'Project'}
                isDisabled={loading || filterLoading}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            {renderProjectData}
          </Dropdown>
          {/* member dropdown */}
          <Dropdown
            modalTitle='Member'
            onChangeSearch={onChangeSearchUser}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Member'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'Member' ? 'dropdown-icon ' : ''}
                onClick={() => setOpenDropdown(openDropdown === 'Member' ? null : 'Member')}
                isActive={openDropdown === 'Member'}
                isDisabled={loading || filterLoading}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            {renderUserData}
          </Dropdown>
        </LeftContent>
        <RightContent>
          {/* custom select dropdown */}
          <Dropdown
            modalTitle='Custom Date'
            isMinWidth={152}
            content={
              <Chip
                title={time_filter?.selected_text || ''}
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                trailingIconName='down-arrow-icon'
                className='custom-dropdown'
                trailingIconClassName={openDropdown === 'status' ? 'dropdown-icon ' : ''}
                onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                isActive={openDropdown === 'status'}
                isDisabled={loading || filterLoading}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            <ReportDurationDropdown
              buttonRef={buttonRef}
              setIsStatusDropdownOpen={() => setOpenDropdown(null)}
              updateDateRangeChange={(startDate: Date, endDate: Date, selected_text: string) =>
                updateDateRangeChange(startDate, endDate, selected_text)
              }
            />
          </Dropdown>
          {/* date range dropdown */}
          <DropdownButton isMonthPicker>
            <Chip
              variant={CHIP_TYPE.FILL}
              size={CHIP_SIZE.SMALL}
              iconColorType='primary'
              iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
              title={`${dayjs(time_filter?.selected_range?.startDate).format('MMM DD')} - ${dayjs(
                time_filter?.selected_range?.endDate
              ).format('MMM DD')}`}
              leadingIconName='calendar-icon'
              trailingIconName='down-arrow-icon'
              trailingIconClassName={openDropdown === 'Date' ? 'dropdown-icon ' : ''}
              onClick={openDateDropdown}
              isActive={openDropdown === 'Date'}
              className='calendar-dropdown'
              isDisabled={loading || filterLoading}
            />
            {openDropdown === 'Date' && (
              <DateDropBox ref={outsideRef} isLoading={loading}>
                <DateRange
                  editableDateInputs={true}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  ranges={[
                    {
                      startDate: time_filter?.selected_range?.startDate
                        ? new Date(time_filter.selected_range.startDate)
                        : new Date(),
                      endDate: time_filter?.selected_range?.endDate
                        ? new Date(time_filter.selected_range.endDate)
                        : new Date(),
                      key: time_filter?.selected_range?.key || 'selection'
                    }
                  ]}
                  maxDate={new Date()}
                />
              </DateDropBox>
            )}
          </DropdownButton>
        </RightContent>
      </ContentHeading>
    );
  }, [
    onChangeSearchProject,
    openDropdown,
    loading,
    filterLoading,
    renderProjectData,
    onChangeSearchUser,
    renderUserData,
    time_filter?.selected_text,
    time_filter?.selected_range?.startDate,
    time_filter?.selected_range?.endDate,
    time_filter?.selected_range?.key,
    openDateDropdown,
    handleSelect,
    updateDateRangeChange
  ]);

  return (
    <>
      <AppLayout>
        {!isMobile ? (
          <Header>
            <HeaderLeft>
              <HeaderTitle>Time</HeaderTitle>
            </HeaderLeft>
            {!isMobile && renderFilterOptions}
          </Header>
        ) : (
          <ResponsiveHeader title='Time' noMargin={true} />
        )}
        <Content>
          {isMobile && renderFilterOptions}
          {!loading && !dataLoading && (
            <>
              <LineChart projectWiseChartData={projectWiseChartData} onChangeChartType={onChangeChartType} />
              <TableHeader
                timeSheetData={timeSheetData}
                workSummaryData={workSummaryData}
                activityData={activityData}
                onChangeReportFilter={onChangeReportFilter}
              />
            </>
          )}
          {(loading || dataLoading) && <ReportTimelineLoading />}
        </Content>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Time);
